import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertModule,
  ButtonsModule,
  CardModule,
  CheckboxModule,
  DropdownModule,
  IconModule,
  InputModule,
  RadioButtonModule,
  TooltipModule,
} from 'axis-components';
import { AxisValidatorModule } from 'axis-components/customValidators';
import { MediaModule } from 'axis-components/media';
import { SelectionCoveragesService } from '../../../_services/pip/selection-coverages.service';
import { RequestService } from '../../../_services/request/request.service';
import { HeaderSharedService } from '../../../_services/header-shared.service';

import { TooltipLabelComponent } from '../../../_shared/tooltip/tooltip-label.component';
import {
  FormType,
  RequestProgressStep,
  UserRole,
} from '../../../config/constants';
import { PersonalInjuryProtectionSelection } from '../../../config/pip-constants';
import { PipBaseRulesService } from '../../../state-disclosures/pip/services/pip-base-rules.service';
import { NotificationService } from '../../../_services/notification/notification.service';

@Component({
  selector: 'app-pip-selection-coverages',
  templateUrl: './pip-selection-coverages.component.html',
  standalone: true,
  imports: [
    AlertModule,
    AxisValidatorModule,
    ButtonsModule,
    CardModule,
    CheckboxModule,
    CommonModule,
    DropdownModule,
    IconModule,
    InputModule,
    RadioButtonModule,
    ReactiveFormsModule,
    TooltipModule,
    MediaModule,
    TooltipLabelComponent,
  ],
})
export class PipSelectionCoveragesComponent {
  public pipSelectionCoverage: FormGroup;
  public shouldShowCard: boolean = true;
  public shouldShowOtherOptions: boolean = true;
  public shouldShowGarageStates: boolean = false;

  public stateList = [
    { name: 'Arizona', id: 'AZ' },
    { name: 'California', id: 'CA' },
    { name: 'North Carolina', id: 'NC' },
    { name: 'Texas', id: 'TX' },
  ];

  data: any;
  garageStatesData: any;

  // Events emitted to playground component for workflow control
  @Output() continueClickedPlayground = new EventEmitter<void>();
  @Output() backClickedPlayground = new EventEmitter<void>();
  @Output() dataChangedPlayground = new EventEmitter<void>();

  private selectionOptions: any[] = [];
  private additionalOptions: any[] = [];
  accountDetails: any = null;
  private userMadeChanges = false;
  private isInitializing = true;

  constructor(
    private readonly selectionCoveragesService: SelectionCoveragesService,
    private router: Router,
    private fb: FormBuilder,
    public baseRulesService: PipBaseRulesService,
    private requestService: RequestService,
    private notificationService: NotificationService,
    private readonly headerSharedService: HeaderSharedService,
  ) {}
  private checkAndResetOutstandingData(): void {
    // Don't emit dataChanged during initial form setup
    if (!this.userMadeChanges || this.isInitializing) {
      return;
    }

    // Generate current data from form (same logic as setDataDump)
    let currentFormData = {};
    for (const [key, value] of Object.entries(
      this.pipSelectionCoverage.value,
    )) {
      currentFormData[key] =
        typeof value === 'object'
          ? this.baseRulesService.getValueOfFormControls(value)
          : value;
    }

    const storedFormData = this.baseRulesService.dataDump?.['data'] || {};

    // Compare and retain selections if data unchanged
    if (JSON.stringify(currentFormData) !== JSON.stringify(storedFormData)) {
      if (this.baseRulesService.dataDump) {
        this.baseRulesService.dataDump['outstandingStates'] = [];
        this.baseRulesService.savedBaseRules = {};
        // Clear summary confirmation when prior selections change
        delete this.baseRulesService.dataDump['summaryConfirmation'];
      }
      this.userMadeChanges = false;

      // Notify playground component when data changes (resets subsequent steps)
      if (this.baseRulesService.isPlayground) {
        this.dataChangedPlayground.emit();
      }
    }
  }
  get hasGarageStates(): boolean {
    return this.requestService.hasGarageStates();
  }
  mapToFormValue(
    controlName: string,
    value: string,
  ): {
    name: string;
    value: string;
    selectedIndex: number;
  } {
    // Determine the appropriate name and selectedIndex based on controlName
    let name: string;
    let selectedIndex: number;
    let options: { value: string; label: string }[];

    if (controlName === 'pipBodilyInjuryVar') {
      options = this.selectionOptions;
      name = 'bodilyInjury';
    } else if (controlName === 'pipAdditionalOptions') {
      options = this.additionalOptions;
      name = 'additionalOptions';
    } else if (controlName === 'garagingSelection') {
      name = 'garaging';
      options = this.garageStatesData?.garagingOptions || [];
      selectedIndex = options.findIndex(
        (option: any) => option.value === value,
      );
    } else if (this.data?.groups?.some((g: any) => g.name === controlName)) {
      const group = this.data.groups.find((g: any) => g.name === controlName);
      options = group?.options || [];
      name = controlName;
    } else {
      name = controlName; // Fallback name
      selectedIndex = 0; // Default index if no options match
    }
    selectedIndex = Math.max(
      0,
      options.findIndex((o) => o.value === value),
    );
    return {
      name,
      value,
      selectedIndex: selectedIndex >= 0 ? selectedIndex : 0,
    };
  }
  ngOnInit(): void {
    this.getAccountDetails();
    // Helper to map datadumpvalues to formgroup
    const unselectedValues: { [key: string]: any } = {};
    if ('data' in this.baseRulesService.dataDump) {
      const data = this.baseRulesService.dataDump['data'];
      Object.keys(data).forEach((key) => {
        if (key) {
          unselectedValues[key] = data[key];
        }
      });
    }
    if (unselectedValues['pipBodilyInjuryVar'] === 'IndividualStateCoverage') {
      this.shouldShowCard = false;
    }
    this.pipSelectionCoverage = this.fb.group({
      pipSelectInsuredName: [
        this.accountDetails.insuredName ||
          unselectedValues['pipSelectInsuredName'] ||
          '',
      ],
      pipSelectInsuredAuthName: [
        this.accountDetails.title ||
          unselectedValues['pipSelectInsuredAuthName'] ||
          '',
      ],
      pipSelectInsuredPolicy: [
        this.accountDetails.insuredCompanyName ||
          unselectedValues['pipSelectInsuredPolicy'] ||
          '',
      ],
      pipSelectBusinessMail: [
        this.accountDetails.insuredEmail ||
          unselectedValues['pipSelectBusinessMail'] ||
          '',
      ],
      pipSelectBusinessNum: [
        this.accountDetails.phoneNumber ||
          unselectedValues['pipSelectBusinessNum'] ||
          this.requestService.phoneNumber ||
          '',
        [Validators.pattern('^[0-9]{10}$')],
      ],
      pipSelectBusinessAdd: [
        this.accountDetails.address ||
          unselectedValues['pipSelectBusinessAdd'] ||
          '',
      ],
      pipSelectBusinessCity: [
        this.accountDetails.city ||
          unselectedValues['pipSelectBusinessCity'] ||
          '',
      ],
      pipSelectBusinessState: [
        this.accountDetails.state ||
          unselectedValues['pipSelectBusinessState'] ||
          '',
      ],
      pipBodilyInjuryVar: [
        this.mapToFormValue(
          'pipBodilyInjuryVar',
          unselectedValues['pipBodilyInjuryVar'] || '',
        ) || '',
        Validators.required,
      ],
      pipAdditionalOptions: [
        this.mapToFormValue(
          'pipAdditionalOptions',
          unselectedValues['pipAdditionalOptions'] || '',
        ) || '',
        Validators.required,
      ],
      garagingSelection: [
        this.mapToFormValue(
          'garagingSelection',
          unselectedValues['garagingSelection'] || '',
        ) || '',
        this.hasGarageStates ? Validators.required : null,
      ],
    });
    if (unselectedValues['pipBodilyInjuryVar'] === 'RejectWherePermissible') {
      this.shouldShowOtherOptions = false;
      this.shouldShowCard = false;
    }

    this.selectionCoveragesService.getSelectionData().subscribe((data) => {
      this.data = data;

      this.selectionOptions = this.data?.selectionOptions || [];
      this.additionalOptions = this.data?.additionalOptions || [];

      for (const group of this.data?.groups || []) {
        if (!this.pipSelectionCoverage.contains(group.name)) {
          this.pipSelectionCoverage.addControl(
            group.name,
            this.fb.control(
              this.mapToFormValue(group.name, unselectedValues[group.name]) ||
                '',
              Validators.required,
            ),
          );
        }
      }

      // Subscribe to pipBodilyInjuryVar changes to manage card visibility and reset
      this.pipSelectionCoverage
        .get('pipBodilyInjuryVar')
        ?.valueChanges.subscribe((val) => {
          const chosenValue = val && val.value ? val.value : val;
          this.shouldShowCard = chosenValue !== 'IndividualStateCoverage';
          if (chosenValue === 'IndividualStateCoverage') {
            this.resetGroupControls();
          }
          // Reset other controls when RejectWherePermissible is selected
          if (chosenValue === 'RejectWherePermissible') {
            this.resetGroupControls();
            // Also reset pipAdditionalOptions
            this.pipSelectionCoverage.patchValue(
              {
                pipAdditionalOptions: '',
              },
              { emitEvent: false },
            );
          }
        });
      const initialFormValue = this.pipSelectionCoverage.getRawValue();

      this.pipSelectionCoverage.valueChanges.subscribe(() => {
        const currentFormValue = this.pipSelectionCoverage.getRawValue();

        const hasChanged = Object.keys(currentFormValue).some((key) => {
          // For objects (like dropdowns/radios), compare their .value property
          const initial = initialFormValue[key];
          const current = currentFormValue[key];
          if (initial && typeof initial === 'object' && 'value' in initial) {
            return initial.value !== (current?.value ?? current);
          }
          return initial !== current;
        });

        // Check if any control is valid and has been interacted with
        const anyValid = Object.values(this.pipSelectionCoverage.controls).some(
          (control: any) => control.valid,
        );
        if (
          !this.notificationService.PIPSelectionCoverageNotificationFlag &&
          this.requestService.getUserRole() === UserRole.BROKER &&
          anyValid &&
          hasChanged
        ) {
          this.notificationService.PIPSelectionCoverageNotificationFlag = true;
          this.notificationService.showNotification(
            'Please note that the insured must fill out this form using the link sent to their inbox. All changes made to this view will not be saved.',
            'warning',
            'Selections Will Not Be Saved',
          );
        }
        // Only mark as user changes after initial setup is complete
        if (!this.isInitializing) {
          this.userMadeChanges = true;
        }

        // Reset subsequent steps whenever form values change (AC2)
        if (hasChanged) {
          this.headerSharedService.resetSteps('pip');
        }

        // Check and reset outstanding data when form values change
        if (this.baseRulesService.isPlayground) {
          this.checkAndResetOutstandingData();
        }
      });
    });
    this.pipSelectionCoverage
      .get('pipSelectBusinessNum')
      ?.valueChanges.subscribe((value) => {
        const control = this.pipSelectionCoverage.get('pipSelectBusinessNum');
        if (value && control) {
          control.setValidators([
            Validators.pattern('^[0-9]{10}$|^[0-9]{3}-[0-9]{3}-[0-9]{4}$'),
          ]);
          if (this.requestService.request?.requestDetails?.accountDetail) {
            this.requestService.request.requestDetails.accountDetail.phoneNumber =
              value || this.requestService.phoneNumber;
          }
        } else if (control) {
          // Clear validators when the field is empty
          control.clearValidators();
          if (this.requestService.request?.requestDetails?.accountDetail) {
            this.requestService.request.requestDetails.accountDetail.phoneNumber =
              '';
          }
        }
        control?.updateValueAndValidity({ emitEvent: false });
      });
    // Load garage states data
    this.selectionCoveragesService
      .getGarageStatesData()
      .subscribe((garageData) => {
        this.garageStatesData = garageData;
        this.shouldShowGarageStates = this.hasGarageStates;
      });

    this.goTop();

    // Mark initialization as complete after form setup
    setTimeout(() => {
      this.isInitializing = false;
    }, 0);
  }
  getAccountDetails(): any {
    if (
      this.requestService.request &&
      this.requestService.request.requestDetails &&
      this.requestService.request.requestDetails.accountDetail
    ) {
      this.accountDetails =
        this.requestService.request.requestDetails.accountDetail;
      return this.accountDetails;
    }
    return null;
  }
  updateRequestDetailsWithFormData() {
    const stateValue = this.pipSelectionCoverage.value.pipSelectBusinessState;
    const stateCode =
      typeof stateValue === 'object' && stateValue !== null
        ? stateValue.id // Extract the state code if it's an object
        : stateValue;
    // Find the matching state object in stateList using the state code
    const stateObject = this.stateList.find((state) => state.id === stateCode);
    const stateName = stateObject ? stateObject.name : stateCode;
    const phoneNumber =
      this.pipSelectionCoverage.value.pipSelectBusinessNum || '';
    // Get account detail values from the form
    const accountDetails = {
      accountDetail: {
        insuredName: this.pipSelectionCoverage.value.pipSelectInsuredName,
        title: this.pipSelectionCoverage.value.pipSelectInsuredAuthName,
        name: this.pipSelectionCoverage.value.pipSelectInsuredPolicy,
        insuredEmail: this.pipSelectionCoverage.value.pipSelectBusinessMail,
        phoneNumber: phoneNumber,
        address: this.pipSelectionCoverage.value.pipSelectBusinessAdd,
        city: this.pipSelectionCoverage.value.pipSelectBusinessCity,
        state: stateName,
      },
    };
    // Update request details with new account values
    this.requestService.updateRequestDetails(accountDetails);
  }
  goTop(): void {
    window.scrollTo(0, 0);
  }
  checkPreselect(option: any): boolean {
    const formValues = this.pipSelectionCoverage?.value || {};
    return (
      option?.value === formValues.pipBodilyInjuryVar?.value ||
      option?.value === formValues.pipAdditionalOptions?.value ||
      option?.value === formValues.garagingSelection?.value
    );
  }

  patchValue(controlName: string, value: string): void {
    const formValue = this.mapToFormValue(controlName, value);
    this.pipSelectionCoverage.patchValue({ [controlName]: formValue });

    this.changeRadioLabelDynamically(controlName, formValue);
  }

  private changeRadioLabelDynamically(
    controlName: string,
    formValue: { name: string; value: string; selectedIndex: number },
  ) {
    if (controlName === 'pipBodilyInjuryVar') {
      if (
        formValue.value ===
        PersonalInjuryProtectionSelection.RejectWherePermissible
      ) {
        // Hide other radio options when RejectWherePermissible is selected
        this.shouldShowOtherOptions = false;
        this.shouldShowCard = false;
        // Reset other form controls when RejectWherePermissible is selected
        this.resetGroupControls();
        const additionalControl = this.pipSelectionCoverage.get(
          'pipAdditionalOptions',
        );
        if (additionalControl) {
          additionalControl.patchValue('', { emitEvent: false });
          additionalControl.setErrors(null); // Clear validation errors
          additionalControl.markAsUntouched(); // Remove touched state
        }
      } else if (
        formValue.value === PersonalInjuryProtectionSelection.AcceptCoverage
      ) {
        this.shouldShowOtherOptions = true;
        this.shouldShowCard = true;
      } else if (
        formValue.value ===
        PersonalInjuryProtectionSelection.IndividualStateCoverage
      ) {
        this.shouldShowOtherOptions = true;
        this.shouldShowCard = false; // Hide card but show other options

        // Reset group controls for IndividualStateCoverage
        this.resetGroupControls();
      } else {
        // Show all radio options for any other selection
        this.shouldShowOtherOptions = true;
        this.shouldShowCard = true;
      }
    }
  }

  onBack(): void {
    if (this.baseRulesService.isPlayground) {
      this.backClickedPlayground.emit();
      return;
    }
    this.router.navigate(['/insured/pip-consent'], {
      queryParamsHandling: 'preserve',
    });
  }

  onContinue(): void {
    this.checkAndResetOutstandingData();
    // Skip validation for phone number
    const phoneControl = this.pipSelectionCoverage.get('pipSelectBusinessNum');
    if (phoneControl) {
      phoneControl.clearValidators();
      phoneControl.updateValueAndValidity();
    }
    // Mark all controls as touched to trigger validation, but skip validation for hidden fields
    Object.keys(this.pipSelectionCoverage.controls).forEach((key) => {
      const control = this.pipSelectionCoverage.get(key);
      const isHiddenGroupControl =
        !this.shouldShowCard &&
        this.data?.groups?.some((g: any) => g.name === key);

      const isHiddenAdditionalOptions =
        !this.shouldShowOtherOptions && key === 'pipAdditionalOptions';

      const isHiddenGaragingSelection =
        !this.shouldShowGarageStates && key === 'garagingSelection';

      if (
        isHiddenGroupControl ||
        isHiddenAdditionalOptions ||
        isHiddenGaragingSelection
      ) {
        control?.setErrors(null);
        control?.markAsUntouched();
      } else {
        control?.markAsTouched();
      }
    });
    // Only validate visible radio fields
    if (this.shouldShowCard) {
      this.data?.groups?.forEach((group: any) => {
        const control = this.pipSelectionCoverage.get(group.name);
        if (control) {
          const selectedValue = control.value?.value;
          if (!selectedValue) {
            control.setErrors({ required: true });
          }
        }
      });
    }

    // Validate pipBodilyInjuryVar (always required)
    const bodilyControl = this.pipSelectionCoverage.get('pipBodilyInjuryVar');
    if (bodilyControl) {
      const selectedValue = bodilyControl.value?.value;
      if (!selectedValue) {
        bodilyControl.setErrors({ required: true });
      }
    }

    // Only validate pipAdditionalOptions if shouldShowOtherOptions is true
    if (this.shouldShowOtherOptions) {
      const additionalControl = this.pipSelectionCoverage.get(
        'pipAdditionalOptions',
      );
      if (additionalControl) {
        const selectedValue = additionalControl.value?.value;
        if (!selectedValue) {
          additionalControl.setErrors({ required: true });
        }
      }
    }

    // Validate garaging selection if the card is shown
    if (this.shouldShowGarageStates) {
      const garagingControl =
        this.pipSelectionCoverage.get('garagingSelection');
      if (garagingControl) {
        const selectValue = garagingControl.value?.value;
        if (!selectValue) {
          garagingControl.setErrors({ required: true });
        }
      }
    } else {
      // Clear garaging selection errors when garage states are not shown
      const garagingControl =
        this.pipSelectionCoverage.get('garagingSelection');
      if (garagingControl) {
        garagingControl.setErrors(null);
        garagingControl.markAsUntouched();
      }
    }

    if (this.pipSelectionCoverage.invalid) {
      window.scrollTo(0, 0);
      return;
    }

    this.updateRequestDetailsWithFormData();
    this.baseRulesService.coverageLimit =
      this.pipSelectionCoverage.value.coverageLimit.value;
    this.baseRulesService.personalInjuryProtectionSelection =
      this.pipSelectionCoverage.value.pipBodilyInjuryVar.value;
    this.baseRulesService.additionalOptions =
      this.pipSelectionCoverage.value.pipAdditionalOptions.value;
    if (this.shouldShowGarageStates) {
      this.baseRulesService.garagingSelectionKey =
        this.pipSelectionCoverage.get('garagingSelection').value.value;
    }
    this.setDataDump();
    this.setCoverageSelection();

    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.continueClickedPlayground.emit();
      return;
    }
    this.requestService.refreshRequestData(false).subscribe({
      next: () => {
        this.handleContinueWithFreshStep();
      },
      error: () => {
        this.handleContinueWithFreshStep();
      },
    });
  }

  private handleContinueWithFreshStep(): void {
    // Read current DB-backed step snapshot before moving the form ahead.
    const isPipCompleted = this.requestService.isFormTypeAlreadyCompleted(
      FormType.PIP,
    );

    // If PIP is already completed, do not continue with consent routing.
    if (isPipCompleted) {
      return;
    }

    this.requestService.updateRequestProgress(
      FormType.PIP,
      RequestProgressStep.SELECTION_OF_COVERAGES,
      this.baseRulesService.dataDump,
    );

    this.router.navigate(['/insured/pip-review-state'], {
      queryParamsHandling: 'preserve',
    });
  }
  setDataDump() {
    let formData = {};
    for (const [key, value] of Object.entries(
      this.pipSelectionCoverage.value,
    )) {
      formData[key] =
        typeof value === 'object'
          ? this.baseRulesService.getValueOfFormControls(value)
          : value;
    }
    formData = { data: formData };
    this.baseRulesService.dataDump = {
      ...this.baseRulesService.dataDump,
      ...formData,
    };
  }
  private resetGroupControls(): void {
    const groups = this.data?.groups || [];
    // Reset group controls
    groups.forEach((group: any) => {
      const control = this.pipSelectionCoverage.get(group.name);
      if (control) {
        control.patchValue('', { emitEvent: false });
        control.setErrors(null); // Clear validation errors
        control.markAsUntouched(); // Remove touched state
        control.markAsPristine(); // Mark as pristine
      }
    });
  }

  setCoverageSelection() {
    this.baseRulesService.coverageLimitKey =
      this.pipSelectionCoverage.value.coverageLimit.value || 'Null';
    this.baseRulesService.selectionTypeKey =
      this.pipSelectionCoverage.value.pipBodilyInjuryVar.value || 'Null';
    this.baseRulesService.AdditionalOptionsKey =
      this.pipSelectionCoverage.value.pipAdditionalOptions.value || 'Null';
    if (this.shouldShowGarageStates) {
      this.baseRulesService.garagingSelectionKey =
        this.pipSelectionCoverage.get('garagingSelection').value.value;
    }
  }
}
